import { useEffect, useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import type { PSDGeneral } from '../../../types/psd';
import {
  preparationOptions,
  hydrometerTypeOptions,
  dispersionMethodOptions,
} from '../config/psdFields';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

interface Props {
  data: PSDGeneral;
  onChange: (field: keyof PSDGeneral, value: string | number | boolean | null) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

// ─── Shared input styling ────────────────────────────────────────────────────
// BUG-2026-0061 — same fix as TriaxialParametersTab: outlined-label shrunk
// state sits above the input's top border, so a 4 px row gap caused row
// N+1's label to overlap row N's input. Bumped vertical padding + label size
// here and switched the 2-column field grids below to a `fieldGridSx` with
// a 10 px rowGap. Atterberg's parameters page is unaffected — it already
// uses gap: 1–1.5.
const inputSx = {
  '& .MuiInputBase-input': { py: 0.6, px: 1, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.84rem' },
  '& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
  '& .MuiInputBase-input[type=number]': { MozAppearance: 'textfield' },
};

// 2-column field grid: row gap large enough to clear the shrunk label of
// the row below; column gap stays tight so paper sections don't grow wide.
const fieldGridSx = { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 0.5, rowGap: 1.25 } as const;

const unit = (s: string) => (
  <InputAdornment position="end">
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>{s}</Typography>
  </InputAdornment>
);

const sectionHeader = (s: string, color: string = 'primary.main') => (
  <Typography variant="overline" sx={{
    fontWeight: 700, color, fontSize: '0.7rem', letterSpacing: 0.6,
  }}>
    {s}
  </Typography>
);

const subsectionHeader = (s: string) => (
  <Typography sx={{
    fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary',
    textTransform: 'uppercase', letterSpacing: 0.4, mt: 1.25, mb: 0.5,
  }}>
    {s}
  </Typography>
);

const computedDisplay = (label: string, value: string, unitText?: string) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.3 }}>
    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {unitText && <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{unitText}</Typography>}
    </Box>
  </Box>
);

// ─── Computations (live preview only) ───────────────────────────────────────
const fmt = (v: number | null, d = 2): string =>
  v === null || Number.isNaN(v) ? '—' : v.toFixed(d);

function computeWaterContent(general: PSDGeneral): number | null {
  const c = general.massOfContainer;
  const w = general.massOfWetSoilAndContainer;
  const d = general.massOfDrySoilAndContainer;
  if (c == null || w == null || d == null) return null;
  const dry = d - c;
  if (dry <= 0) return null;
  return ((w - d) / dry) * 100;
}

// FEAT-2026-0005 — Total wet/dry mass now derive from the container fields the
// user already enters in Sieve Setup & Water Content (Mass of Wet/Dry Soil +
// Container minus Mass of Container). Legacy pan-based fields are kept as a
// fallback so historical rows that pre-date the consolidation still compute.
function computeTotalWetMass(general: PSDGeneral): number | null {
  if (general.massOfWetSoilAndContainer != null && general.massOfContainer != null) {
    return general.massOfWetSoilAndContainer - general.massOfContainer;
  }
  if (general.totalMassWetSampleAndPan != null && general.massOfPan != null) {
    return general.totalMassWetSampleAndPan - general.massOfPan;
  }
  return null;
}

function computeTotalDryMass(general: PSDGeneral): number | null {
  if (general.massOfDrySoilAndContainer != null && general.massOfContainer != null) {
    return general.massOfDrySoilAndContainer - general.massOfContainer;
  }
  if (general.totalMassAirOvenDrySampleAndPan != null && general.massOfPan != null) {
    return general.totalMassAirOvenDrySampleAndPan - general.massOfPan;
  }
  return null;
}

// BUG-2026-0022 — 4 derived fields in the 1st-Separation panel that match
// formulas in `qa/2ndtest/PSD_Input.xlsm` ("Sieve (2)" sheet, J39 + J45):
//   pre1stSepMassBelowDry      = totalDryMass - pre1stSepDryMassRetained          (J39)
//   massOfSubsampleBelow1st    = airOvenDrySubsamplePlusPan - massOfSubsamplePan
//   dryMassPassing1st          = pre1stSepMassBelowDry / (1 + WC/100)             (J45)
//   dryMassOfSubsampleBelow1st = massOfSubsampleBelow1st / (1 + WC/100)
function computePre1stSepMassBelowDry(general: PSDGeneral): number | null {
  const totalDry = computeTotalDryMass(general);
  if (totalDry === null || general.pre1stSepDryMassRetained == null) return null;
  return totalDry - general.pre1stSepDryMassRetained;
}
function computeMassOfSubsampleBelow1st(general: PSDGeneral): number | null {
  if (general.airOvenDrySubsamplePlusPan == null || general.massOfSubsamplePan == null) return null;
  return general.airOvenDrySubsamplePlusPan - general.massOfSubsamplePan;
}
/** Convert air-dry → oven-dry by dividing by (1 + WC/100). When WC isn't set
 *  the input is returned unchanged (assumes already oven-dry). */
function applyWaterContentCorrection(airDry: number | null, wc: number | null): number | null {
  if (airDry === null) return null;
  if (wc === null || wc <= 0) return airDry;
  return airDry / (1 + wc / 100);
}

// BUG-2026-0023 — derived field in the 2nd-Separation panel
// (`qa/2ndtest/PSD_Input.xlsm`, "Sieve (2)" sheet, J53):
//   ifWholeSampleWashedMassAbove75um = washedDryAndContainerMass2nd - massOfPan
function computeIfWholeSampleWashedMassAbove75um(general: PSDGeneral): number | null {
  if (general.washedDryAndContainerMass2nd == null || general.massOfPan == null) return null;
  return general.washedDryAndContainerMass2nd - general.massOfPan;
}

// ─── Card style ──────────────────────────────────────────────────────────────
const cardSxFor = (theme: Theme, accent?: string) => ({
  p: 1.5, borderRadius: 2,
  border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
  backgroundColor: accent ?? alpha(theme.palette.background.paper, 0.85),
});

// ═══════════════════════════════════════════════════════════════════════════
//  Main component
// ═══════════════════════════════════════════════════════════════════════════
export const PSDParametersTab = ({
  data, onChange, readOnly = false, workOrderDetailKey,
}: Props) => {
  const theme = useTheme();

  // Numeric fields use `<NumericTextField>` (see
  // `frontend/src/components/forms/NumericTextField.tsx`) which sidesteps the
  // `<input type="number">` sanitization bug behind BUG-2026-0029 — typing
  // "14.06" would round-trip through Number("14.0")===14 and visually collapse
  // to 146. The wrapper uses `type="text" inputMode="decimal"` so the browser
  // never touches the user's literal typing. String fields keep `txtVal`/
  // `handleText` because there is no such sanitization concern for them.
  const txtVal = (k: keyof PSDGeneral) => {
    const v = data[k];
    return v === null || v === undefined ? '' : String(v);
  };
  const handleText = (k: keyof PSDGeneral, raw: string) => onChange(k, raw || null);

  const waterContent = useMemo(() => computeWaterContent(data), [data]);
  const totalWet = useMemo(() => computeTotalWetMass(data), [data]);
  const totalDry = useMemo(() => computeTotalDryMass(data), [data]);

  // BUG-2026-0022 — auto-calc the four 1st-Separation derivations.
  const computedPre1stSepMassBelowDry = useMemo(
    () => computePre1stSepMassBelowDry(data),
    [data.totalMassAirOvenDrySampleAndPan, data.massOfPan, data.pre1stSepDryMassRetained],
  );
  const computedMassOfSubsampleBelow1st = useMemo(
    () => computeMassOfSubsampleBelow1st(data),
    [data.airOvenDrySubsamplePlusPan, data.massOfSubsamplePan],
  );
  const computedDryMassPassing1st = useMemo(
    () => applyWaterContentCorrection(computedPre1stSepMassBelowDry, waterContent),
    [computedPre1stSepMassBelowDry, waterContent],
  );
  const computedDryMassOfSubsampleBelow1st = useMemo(
    () => applyWaterContentCorrection(computedMassOfSubsampleBelow1st, waterContent),
    [computedMassOfSubsampleBelow1st, waterContent],
  );

  // BUG-2026-0023 — auto-calc the 2nd-Separation "If whole sample washed" mass.
  const computedIfWholeSampleWashedMassAbove75um = useMemo(
    () => computeIfWholeSampleWashedMassAbove75um(data),
    [data.washedDryAndContainerMass2nd, data.massOfPan],
  );

  // Sync derived values back into parent state so saves persist them. Each
  // useEffect short-circuits when the incoming data already matches the
  // computed value, preventing render loops.
  useEffect(() => {
    if (data.pre1stSepMassBelowDry !== computedPre1stSepMassBelowDry) {
      onChange('pre1stSepMassBelowDry', computedPre1stSepMassBelowDry);
    }
  }, [computedPre1stSepMassBelowDry, data.pre1stSepMassBelowDry, onChange]);
  useEffect(() => {
    if (data.massOfSubsampleBelow1st !== computedMassOfSubsampleBelow1st) {
      onChange('massOfSubsampleBelow1st', computedMassOfSubsampleBelow1st);
    }
  }, [computedMassOfSubsampleBelow1st, data.massOfSubsampleBelow1st, onChange]);
  useEffect(() => {
    if (data.dryMassPassing1st !== computedDryMassPassing1st) {
      onChange('dryMassPassing1st', computedDryMassPassing1st);
    }
  }, [computedDryMassPassing1st, data.dryMassPassing1st, onChange]);
  useEffect(() => {
    if (data.dryMassOfSubsampleBelow1st !== computedDryMassOfSubsampleBelow1st) {
      onChange('dryMassOfSubsampleBelow1st', computedDryMassOfSubsampleBelow1st);
    }
  }, [computedDryMassOfSubsampleBelow1st, data.dryMassOfSubsampleBelow1st, onChange]);
  useEffect(() => {
    if (data.ifWholeSampleWashedMassAbove75um !== computedIfWholeSampleWashedMassAbove75um) {
      onChange('ifWholeSampleWashedMassAbove75um', computedIfWholeSampleWashedMassAbove75um);
    }
  }, [computedIfWholeSampleWashedMassAbove75um, data.ifWholeSampleWashedMassAbove75um, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* ── COMMON PARAMETERS ────────────────────────────────────────────── */}
      <Box>
        <Typography variant="h6" sx={{
          fontSize: '0.82rem', fontWeight: 800, color: 'text.primary',
          mb: 0.5, letterSpacing: 0.3,
        }}>
          Common Parameters
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, display: 'block' }}>
          Sieve & Hydrometer Setup
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          {/* LEFT — Sieve & Hydrometer Setup */}
          <Paper elevation={0} sx={cardSxFor(theme)}>
            {sectionHeader('Sieve Setup & Water Content')}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', alignItems: 'center', columnGap: 1, rowGap: 0.7, mt: 1 }}>
              <Typography sx={{ fontSize: '0.74rem' }}>ASTM Test Method</Typography>
              <TextField select size="small" value={data.sieveTestMethod ?? 'ASTM D6913-04(2009)'}
                onChange={(e) => onChange('sieveTestMethod', e.target.value)}
                sx={inputSx} disabled={readOnly}>
                <MenuItem value="ASTM D6913-04(2009)" sx={{ fontSize: '0.78rem' }}>ASTM D6913</MenuItem>
              </TextField>

              <Typography sx={{ fontSize: '0.74rem' }}>Preparation (Original)</Typography>
              <TextField select size="small" value={data.preparation ?? ''}
                onChange={(e) => onChange('preparation', e.target.value || null)}
                sx={inputSx} disabled={readOnly}>
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>—</em></MenuItem>
                {preparationOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
                ))}
              </TextField>

              <Typography sx={{ fontSize: '0.74rem' }}>Specimen Taken From</Typography>
              <TextField size="small" value={txtVal('specimenTakenFrom')}
                onChange={(e) => handleText('specimenTakenFrom', e.target.value)}
                placeholder="e.g. Bulk Bag 1" sx={inputSx} disabled={readOnly} />

              <Typography sx={{ fontSize: '0.74rem' }}>Oven Details</Typography>
              <Box sx={fieldGridSx}>
                <TextField size="small" placeholder="Oven No." value={txtVal('ovenNumber')}
                  onChange={(e) => handleText('ovenNumber', e.target.value)}
                  sx={inputSx} disabled={readOnly} />
                <NumericTextField size="small" placeholder="Temp" value={data.ovenTemperatureC}
                  onChange={(v) => onChange('ovenTemperatureC', v)}
                  InputProps={{ endAdornment: unit('°C') }} sx={inputSx} disabled={readOnly} />
              </Box>

              {/* FEAT-2026-0005 — Container ID (uses the legacy PanId column;
                  the lab pan and the soil container are the same physical
                  receptacle, so the field is rebranded rather than duplicated). */}
              <Typography sx={{ fontSize: '0.74rem' }}>Container ID</Typography>
              <TextField size="small" value={txtVal('panId')}
                onChange={(e) => handleText('panId', e.target.value)}
                sx={inputSx} disabled={readOnly} />

              <Typography sx={{ fontSize: '0.74rem' }}>Mass of Container</Typography>
              <NumericTextField size="small" value={data.massOfContainer}
                onChange={(v) => onChange('massOfContainer', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />

              <Typography sx={{ fontSize: '0.74rem' }}>Mass of Wet Soil + Container</Typography>
              <NumericTextField size="small" value={data.massOfWetSoilAndContainer}
                onChange={(v) => onChange('massOfWetSoilAndContainer', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />

              {/* FEAT-2026-0005 — Total Mass of Wet Sample (was in Pre-Test column). */}
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Total Mass of Wet Sample</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography sx={{
                  fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: totalWet === null ? 'text.disabled' : 'text.primary',
                }}>
                  {fmt(totalWet, 2)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>g</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', ml: 0.5 }}>(auto)</Typography>
              </Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Mass of Dry Soil + Container</Typography>
              <NumericTextField size="small" value={data.massOfDrySoilAndContainer}
                onChange={(v) => onChange('massOfDrySoilAndContainer', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />

              {/* FEAT-2026-0005 — Total Air/Oven Dry mass of Sample (was in Pre-Test column). */}
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Total Air/Oven Dry Mass of Sample</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography sx={{
                  fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: totalDry === null ? 'text.disabled' : 'text.primary',
                }}>
                  {fmt(totalDry, 2)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>g</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', ml: 0.5 }}>(auto)</Typography>
              </Box>

              {/* FEAT-2026-0005 — Total dry mass (if not hydro); the canonical
                  hydro flow uses `W` in the Hydrometer card, so the value
                  blanks out when the hydrometer toggle is on (matches
                  `Sieve (2)!B34` in qa/2ndtest/PSD_Input.xlsm). */}
              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Total Dry Mass (if not hydro)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography sx={{
                  fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: data.hydrometerEnabled || totalDry === null ? 'text.disabled' : 'text.primary',
                }}>
                  {data.hydrometerEnabled ? '—' : fmt(totalDry, 2)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>g</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', ml: 0.5 }}>(auto)</Typography>
              </Box>

              <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Water Content</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography sx={{
                  fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: waterContent === null ? 'text.disabled' : 'text.primary',
                }}>
                  {fmt(waterContent, 1)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>%</Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', ml: 0.5 }}>(auto)</Typography>
              </Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Depth Override</Typography>
              <NumericTextField size="small" value={data.depthOverride}
                onChange={(v) => onChange('depthOverride', v)}
                InputProps={{ endAdornment: unit('mm') }} sx={inputSx} disabled={readOnly} />

              <Typography sx={{ fontSize: '0.74rem', alignSelf: 'flex-start', mt: 0.5 }}>Washing</Typography>
              <TextField multiline minRows={3} size="small" value={txtVal('washingNotes')}
                onChange={(e) => handleText('washingNotes', e.target.value)}
                placeholder="Whole test sample / Coarse portion / 1st subsample / 2nd subsample"
                sx={inputSx} disabled={readOnly} />

              <Typography sx={{ fontSize: '0.74rem', alignSelf: 'flex-start', mt: 0.5 }}>Notes</Typography>
              <TextField multiline minRows={2} size="small" value={txtVal('comments')}
                onChange={(e) => handleText('comments', e.target.value)}
                placeholder="Lab notes…" sx={inputSx} disabled={readOnly} />
            </Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, mt: 1 }}>
              Additional Observations (internal — not printed on the report)
            </Typography>
            <TextField multiline minRows={2} size="small" fullWidth
              value={txtVal('additionalObservations')}
              onChange={(e) => handleText('additionalObservations', e.target.value)}
              placeholder="Internal observations — for the lab and reviewer only. The Notes field above prints on the report."
              sx={{ ...inputSx, mt: 0.5 }} disabled={readOnly} />
            {workOrderDetailKey && (
              <ObservationMediaPanel workOrderDetailKey={workOrderDetailKey} readOnly={readOnly} />
            )}
          </Paper>

          {/* RIGHT — Hydrometer Configuration */}
          <Paper elevation={0} sx={cardSxFor(theme,
            data.hydrometerEnabled ? alpha(theme.palette.primary.light, 0.06) : undefined)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              {sectionHeader('Hydrometer Configuration')}
              <FormControlLabel
                control={<Switch size="small" checked={!!data.hydrometerEnabled}
                  onChange={(e) => onChange('hydrometerEnabled', e.target.checked)}
                  disabled={readOnly} />}
                label={<Typography sx={{ fontSize: '0.74rem' }}>Enable Hydrometer</Typography>}
                sx={{ m: 0 }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', alignItems: 'center', columnGap: 1, rowGap: 0.7, opacity: data.hydrometerEnabled ? 1 : 0.5 }}>
              <Typography sx={{ fontSize: '0.74rem' }}>ASTM Test Method</Typography>
              <TextField size="small" value={data.hydroTestMethod ?? 'ASTM D7928'}
                onChange={(e) => handleText('hydroTestMethod', e.target.value)}
                sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />

              <Box sx={{ gridColumn: '1 / -1' }}>{subsectionHeader('Hygroscopic Moisture Content Test')}</Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Hygroscopic mass of wet soil + container</Typography>
              <NumericTextField size="small" value={data.hygroscopicMassWetSoilAndContainer}
                onChange={(v) => onChange('hygroscopicMassWetSoilAndContainer', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />

              <Typography sx={{ fontSize: '0.74rem' }}>Gs Assumed/Measured</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px', columnGap: 0.5, rowGap: 1.25 }}>
                <TextField select size="small" value={data.gsAssumed === null ? '' : data.gsAssumed ? 'assumed' : 'measured'}
                  onChange={(e) => onChange('gsAssumed', e.target.value === '' ? null : e.target.value === 'assumed')}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled}>
                  <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>—</em></MenuItem>
                  <MenuItem value="assumed" sx={{ fontSize: '0.78rem' }}>Assumed</MenuItem>
                  <MenuItem value="measured" sx={{ fontSize: '0.78rem' }}>Measured</MenuItem>
                </TextField>
                <NumericTextField size="small" placeholder="Gs" value={data.gsValue}
                  onChange={(v) => onChange('gsValue', v)}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>{subsectionHeader('Adjustable Constants & Equipment')}</Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Composite Correction (C)</Typography>
              <Box sx={fieldGridSx}>
                <NumericTextField size="small" placeholder="Slope" value={data.compositeCorrectionSlope}
                  onChange={(v) => onChange('compositeCorrectionSlope', v)}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
                <NumericTextField size="small" placeholder="Intercept" value={data.compositeCorrectionIntercept}
                  onChange={(v) => onChange('compositeCorrectionIntercept', v)}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
              </Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Calibration Constants</Typography>
              <Box sx={fieldGridSx}>
                <NumericTextField size="small" placeholder="J1" value={data.calibrationConstantJ1}
                  onChange={(v) => onChange('calibrationConstantJ1', v)}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
                <NumericTextField size="small" placeholder="J2" value={data.calibrationConstantJ2}
                  onChange={(v) => onChange('calibrationConstantJ2', v)}
                  sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
              </Box>

              <Typography sx={{ fontSize: '0.74rem' }}>Hydrometer Number</Typography>
              <TextField size="small" value={txtVal('hydrometerNumber')}
                onChange={(e) => handleText('hydrometerNumber', e.target.value)}
                sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />

              <Typography sx={{ fontSize: '0.74rem' }}>Hydrometer Type</Typography>
              <TextField select size="small" value={data.hydrometerType ?? '152H'}
                onChange={(e) => onChange('hydrometerType', e.target.value)}
                sx={inputSx} disabled={readOnly || !data.hydrometerEnabled}>
                {hydrometerTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
                ))}
              </TextField>

              <Typography sx={{ fontSize: '0.74rem' }}>Method of Dispersion</Typography>
              <TextField select size="small" value={data.methodOfDispersion ?? ''}
                onChange={(e) => onChange('methodOfDispersion', e.target.value || null)}
                sx={inputSx} disabled={readOnly || !data.hydrometerEnabled}>
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>—</em></MenuItem>
                {dispersionMethodOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>{o.label}</MenuItem>
                ))}
              </TextField>

              <Typography sx={{ fontSize: '0.74rem' }}>Dispersing Agent Used</Typography>
              <TextField size="small" value={txtVal('dispersingAgentUsed')}
                onChange={(e) => handleText('dispersingAgentUsed', e.target.value)}
                sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />

              <Typography sx={{ fontSize: '0.74rem' }}>Volume of Dispersant</Typography>
              <NumericTextField size="small" value={data.volumeOfDispersantMl}
                onChange={(v) => onChange('volumeOfDispersantMl', v)}
                InputProps={{ endAdornment: unit('mL') }} sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />

              <Typography sx={{ fontSize: '0.74rem' }}>Dispersion Time</Typography>
              <NumericTextField size="small" value={data.dispersionTimeMin}
                onChange={(v) => onChange('dispersionTimeMin', v)}
                InputProps={{ endAdornment: unit('min') }} sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── PROCESS PARAMETERS (4 panels) ────────────────────────────────── */}
      <Box>
        <Typography variant="h6" sx={{
          fontSize: '0.82rem', fontWeight: 800, color: 'text.primary',
          mb: 0.5, letterSpacing: 0.3, mt: 0.5,
        }}>
          Process Parameters
        </Typography>

        {/* FEAT-2026-0005 — PRE-TEST column removed; its fields now live in
            "Sieve Setup & Water Content" above (Container ID + the three
            derived totals), and the four-column grid shrinks to three. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
          {/* 1ST SEPARATION */}
          <Paper elevation={0} sx={cardSxFor(theme)}>
            <Box sx={{ textAlign: 'center', mb: 0.5 }}>
              {sectionHeader('1st Separation', theme.palette.primary.dark)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              <NumericTextField label="Designated Sieve" size="small"
                value={data.designated1stSeparationSieveMm}
                onChange={(v) => onChange('designated1stSeparationSieveMm', v)}
                InputProps={{ endAdornment: unit('mm') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Dry Mass Retained" size="small"
                value={data.pre1stSepDryMassRetained}
                onChange={(v) => onChange('pre1stSepDryMassRetained', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              {/* BUG-2026-0022: was a manual TextField; now auto-derived. */}
              {computedDisplay('Mass below first Air/Oven dry', fmt(computedPre1stSepMassBelowDry, 2), 'g')}
              <TextField label="Sub Sample Pan ID" size="small" value={txtVal('subSamplePanId')}
                onChange={(e) => handleText('subSamplePanId', e.target.value)} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Mass of Subsample Pan" size="small"
                value={data.massOfSubsamplePan}
                onChange={(v) => onChange('massOfSubsamplePan', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Air/Oven dry subsample + pan" size="small"
                value={data.airOvenDrySubsamplePlusPan}
                onChange={(v) => onChange('airOvenDrySubsamplePlusPan', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              {/* BUG-2026-0022: was a manual TextField; now auto-derived. */}
              {computedDisplay('Mass of subsample below 1st', fmt(computedMassOfSubsampleBelow1st, 2), 'g')}
              <NumericTextField label="Washed dry and container mass" size="small"
                value={data.washedDryAndContainerMass1st}
                onChange={(v) => onChange('washedDryAndContainerMass1st', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              {/* BUG-2026-0022: was manual TextFields; now auto-derived from
                  the upstream values + water content correction. */}
              {computedDisplay('Dry mass passing 1st', fmt(computedDryMassPassing1st, 2), 'g')}
              {computedDisplay('Dry mass of subsample below 1st', fmt(computedDryMassOfSubsampleBelow1st, 2), 'g')}
            </Box>
          </Paper>

          {/* 2ND SEPARATION */}
          <Paper elevation={0} sx={cardSxFor(theme)}>
            <Box sx={{ textAlign: 'center', mb: 0.5 }}>
              {sectionHeader('2nd Separation', theme.palette.primary.dark)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              <NumericTextField label="Designated Sieve" size="small"
                value={data.designated2ndSeparationSieveMm}
                onChange={(v) => onChange('designated2ndSeparationSieveMm', v)}
                InputProps={{ endAdornment: unit('mm') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Dry mass retained" size="small"
                value={data.pre2ndSepDryMassRetained}
                onChange={(v) => onChange('pre2ndSepDryMassRetained', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Dry mass passing" size="small"
                value={data.pre2ndSepDryMassPassing}
                onChange={(v) => onChange('pre2ndSepDryMassPassing', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Dry mass of subsample below 2nd" size="small"
                value={data.dryMassOfSubsampleBelow2nd}
                onChange={(v) => onChange('dryMassOfSubsampleBelow2nd', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Dry mass above 75µm after washing" size="small"
                value={data.dryMassAbove75umAfterWashing}
                onChange={(v) => onChange('dryMassAbove75umAfterWashing', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              <NumericTextField label="Washed dry and container mass" size="small"
                value={data.washedDryAndContainerMass2nd}
                onChange={(v) => onChange('washedDryAndContainerMass2nd', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly} />
              {/* BUG-2026-0023: was a manual TextField; now auto-derived. */}
              {computedDisplay('If whole sample washed, mass above 75µm', fmt(computedIfWholeSampleWashedMassAbove75um, 2), 'g')}
            </Box>
          </Paper>

          {/* HYDROMETER */}
          <Paper elevation={0} sx={cardSxFor(theme,
            data.hydrometerEnabled ? alpha(theme.palette.primary.light, 0.06) : undefined)}>
            <Box sx={{ textAlign: 'center', mb: 0.5 }}>
              {sectionHeader('Hydrometer', theme.palette.primary.dark)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, opacity: data.hydrometerEnabled ? 1 : 0.5 }}>
              <NumericTextField label="Initial mass of specimen below 2mm" size="small"
                value={data.initialMassOfSpecimenBelow2mm}
                onChange={(v) => onChange('initialMassOfSpecimenBelow2mm', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
              <NumericTextField label="Total dry mass represented in hydro, W" size="small"
                value={data.totalDryMassRepresentedInHydroW}
                onChange={(v) => onChange('totalDryMassRepresentedInHydroW', v)}
                InputProps={{ endAdornment: unit('g') }} sx={inputSx} disabled={readOnly || !data.hydrometerEnabled} />
              {computedDisplay('Actual Dry Mass', fmt(data.initialMassOfSpecimenBelow2mm, 2), 'g')}
              {computedDisplay('Calculated Representative Mass', fmt(data.totalDryMassRepresentedInHydroW, 2), 'g')}
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 1, fontStyle: 'italic' }}>
                Calculations will update automatically as fields are filled.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

    </Box>
  );
};
